<template>
  <div
    ref="scrollContainer"
    class="flex flex-col gap-x-2 overflow-y-auto flex-1"
    @scroll="debouncedHandleScroll"
  >
    <div class="my-2 flex items-center justify-between gap-x-3">
      <BaseButton
        v-if="localContactId"
        :title="$t('button.reset')"
        class="mt-0.5"
        icon="trash"
        color="warning"
        @click="localContactId = null"
      />
      <BaseSimpleSearchInput v-model:model-value="filterText" @input="debouncedSearch(filterText)"/>
    </div>
    <BaseButton
      :title="$t('global.all_contacts')"
      :icon="!funeralHomeFilter ? 'eye' : 'eye-slash'"
      class="mt-0.5"
      @click="toggleAllContacts"
    />
    <template v-if="filteredContact.length < 1">
      <div class="mx-auto mt-12 text-center text-slate-300">
        {{
          $t('global.there_is_no_yet', {
            settings: roleId ? roleName?.toLowerCase() : $t('global.contact')
          })
        }}
        <span v-if="funeralHomeId">
          <span>{{ $t('global.for_funeral_home') + ' ' }}</span>
          <span class="font-bold">{{
            metadata?.models?.funeralHomes?.filter(
              (fh) => fh.id === funeralHomeId
            )[0]?.name
          }}</span>
        </span>
      </div>
    </template>
    <template v-else>
      <div class="grid grid-cols-1 gap-4 py-4 md:grid-cols-2">
        <template v-for="contact in filteredContact" :key="contact.id">
          <SelectContactCard
            :contact="contact"
            :icon="icon"
            :selected="contact.id === localContactId"
            @edit-contact="handleEditContact"
            @click="selectContact(contact.id)"
          />
        </template>
      </div>
    </template>
    <div
      v-if="loadingMore && currentPage > 1"
      class="flex justify-center py-2"
    >
      <LoadingComponent :display-label="false" size="small" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import SelectContactCard from '@ui/modal/contact/components/SelectContactCard.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import BaseSimpleSearchInput from '@c/addf-package/components/BaseInput/BaseSimpleSearchInput.vue'
import { debounce } from '@u/debounce.js'
import LoadingComponent from '@c/TheLoadingComponent.vue'

export default {
  name: 'SelectContactList',
  components: { LoadingComponent, BaseSimpleSearchInput, BaseButton, SelectContactCard },
  props: {
    contactId: {
      type: [String, Number],
      required: true
    },
    roleId: {
      type: [String, Number],
      required: false,
      default: null
    },
    funeralHomeId: {
      type: [String, Number],
      required: false,
      default: null
    },
    icon: {
      type: String,
      required: false,
      default: 'user'
    },
    roleName: {
      type: String,
      required: false,
      default: null
    }
  },
  emits: [
    'update:contactId',
    'selectContactContact',
    'editContact',
    'edit-contact'
  ],
  data() {
    return {
      filterText: '',
      contacts: [],
      currentPage: 1,
      lastPage: 1,
      perPage: 100,
      loadingMore: false,
      allContactsLoaded: false,
      allLoadedContacts: []
    }
  },
  computed: {
    ...mapGetters({
      roleContactList: 'contact/getRoleContactList',
      metadata: 'metadata/getInitialMetadata',
      dossier: 'dossier/getDossier'
    }),
    localContactId: {
      get() {
        return this.contactId
      },
      set(value) {
        this.$emit('update:contactId', value)
      }
    },
    filteredContact() {
      let searchTerm = this.$h.normalizeSearch(this.filterText)
      return this.contacts.filter((contact) => {
        return (
          this.$h.normalizeSearch(contact?.company_name)?.includes(searchTerm) ||
          this.$h.normalizeSearch(contact?.last_name)?.includes(searchTerm) ||
          this.$h.normalizeSearch(contact?.first_name)?.includes(searchTerm)
        )
      })
    },
    funeralHomeFilter() {
      return !!this.funeralHomeId
    }
  },
 mounted() {
   this.fetchContactList()
  },
  created() {
    this.debouncedSearch = debounce((term) => {
      term = term.trim()

      if (this.allContactsLoaded) {
        if (!term) {
          this.contacts = [...this.allLoadedContacts]
        } else {
          this.contacts = this.allLoadedContacts.filter(contact =>
            [contact.company_name, contact.last_name, contact.first_name]
              .some(field => this.$h.normalizeSearch(field)?.includes(this.$h.normalizeSearch(term)))
          )
        }
      } else {
        this.currentPage = 1
        // this.contacts = []
        this.fetchContactList(false, term)
      }
    }, 500)

      this.debouncedHandleScroll = debounce(this.handleScroll, 200)
  },
  methods: {
    ...mapActions({
      processFetchContactListAction: 'contact/fetchContactList'
    }),
    async fetchContactList(loadMore = false, term = '') {
      if (!loadMore) {
        if (!term) {
          this.currentPage = 1
          this.allContactsLoaded = false
          this.loading = true
          // this.contacts = []
        } else if (this.currentPage < this.lastPage) {
          this.loading = true
          // this.contacts = []
        }
      } else {
        this.loadingMore = true
      }

      if (!term || this.currentPage < this.lastPage) {
        try {
          const response = await this.processFetchContactListAction({
            roleId: this.roleId,
            search: term,
            page: this.currentPage,
            per_page: this.perPage,
            pagination: true
          })

          const newContacts = response.contacts || []
          if (loadMore) {
            this.contacts = [...(this.contacts || []), ...newContacts]
            this.allLoadedContacts = [
              ...(this.allLoadedContacts || []),
              ...newContacts
            ]
          } else {
            this.contacts = newContacts
            if (!term) this.allLoadedContacts = newContacts
          }

          if (!term) {
            this.currentPage = response.meta?.current_page
            this.lastPage = response.meta?.last_page
            this.allContactsLoaded = this.currentPage >= this.lastPage
          }

        } finally {
          this.loading = false
          this.loadingMore = false
        }
      }
      //
      // this.processFetchContactListAction({
      //   roleId: this.roleId
      // }).then((response) => {
      //   console.log(response)
      //   this.contacts = response.contacts
      // })
    },
    handleEditContact(contact_id) {
      this.$emit('edit-contact', contact_id)
    },
    selectContact(contact_id) {
      this.localContactId = contact_id
    },
    toggleAllContacts() {
      console.log('toggleAllContacts')
    },
    loadMore() {
      if (this.currentPage < this.lastPage) {
        this.currentPage++
        this.fetchContactList(true, this.filterText)
      }
    },
    handleScroll() {
      // if(!this.filterText) {
      console.log('ici')
      const container = this.$refs.scrollContainer
      if (!container) return

      const bottomReached =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 400

      if (bottomReached && !this.loading && this.currentPage < this.lastPage) {
        this.loadMore()
      }
      // }
    }
  }
}
</script>
